<template>
  <div class="container py-4">
    <div class="page-card">
      <div
        class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2"
      >
        <h4 class="mb-0">Cash drawer</h4>
        <!-- <b-button size="sm" variant="outline-secondary" @click="load">Refresh</b-button> -->
        <icon-button
          :func="() => load()"
          :icon="'rotate-right'"
          :title="'Refresh'"
          :classes="'akkurate-green-btn'"
          :btnName="''"
        />
      </div>

      <div v-if="currentSession" class="mb-4 p-3 border rounded">
        <div
          class="d-flex justify-content-between align-items-start flex-wrap gap-2"
        >
          <div>
            <b-badge variant="success">Shift open</b-badge>
            <p class="mb-1 mt-2 small text-muted">
              Opened {{ formatDate(currentSession?.openedAt) }} by
              {{ currentSession?.userName }}
            </p>
          </div>
          <!-- <b-button
            size="sm"
            class="akkurate-green-btn"
            @click=""
          >
            Close shift
          </b-button> -->
          <icon-button
            :func="() => (showClose = true)"
            :icon="'times'"
            :title="'Close Shift'"
            :classes="'akkurate-green-btn btn-size2'"
            :btnName="'Close shift'"
          />
        </div>
        <div class="row mt-3 g-3">
          <div class="col-md-3">
            <div class="small text-muted">Opening float</div>
            <div class="fw-semibold">
              {{ formatMoney(currentSession?.openingFloat) }}
            </div>
          </div>
          <div class="col-md-3">
            <div class="small text-muted">Expected cash</div>
            <div class="fw-semibold">
              {{ formatMoney(currentSession?.expectedCash) }}
            </div>
          </div>
        </div>
      </div>

      <div v-else class="mb-4 p-3 border rounded">
        <p class="mb-2 text-muted">No open shift for this store.</p>
        <icon-button
          :func="() => (showOpen = true)"
          :icon="'plus'"
          :title="'Open shift'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Open shift'"
        />
        <!-- <b-button class="akkurate-green-btn btn-size" @click="showOpen = true">
          Open shift
        </b-button> -->
      </div>

      <h6>Recent sessions</h6>
      <b-table
        :items="sessions"
        :fields="sessionFields"
        :busy="loading"
        small
        responsive
        hover
      >
        <template #cell(openingFloat)="data">{{
          formatMoney(data.value)
        }}</template>
        <template #cell(expectedCash)="data">{{
          formatMoney(data.value)
        }}</template>
        <template #cell(countedCash)="data">
          {{ data.value != null ? formatMoney(data.value) : "—" }}
        </template>
        <template #cell(variance)="data">
          <span v-if="data.value != null" :class="varianceClass(data.value)">
            {{ formatMoney(data.value) }}
          </span>
          <span v-else>—</span>
        </template>
        <template #cell(status)="data">
          <b-badge
            :variant="data.value === 'open' ? 'success' : 'secondary'"
            :class="statusBadge(data.value == 'open')"
          >
            {{ data.value.toUpperCase() }}
          </b-badge>
        </template>
        <template #cell(openedAt)="data">{{ formatDate(data.value) }}</template>
      </b-table>
    </div>

    <b-modal v-model="showOpen" title="Open cash drawer" @ok="openShift">
      <b-form-group label="Opening float">
        <b-form-input
          class="form-control"
          v-model="openForm.openingFloat"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-textarea v-model="openForm.notes" rows="2" />
      </b-form-group>

      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="''"
          :title="'close'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Close'"
        />
        <icon-button
          :func="() => ok()"
          :icon="''"
          :title="'proceed'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Proceed'"
        />
      </template>
    </b-modal>

    <b-modal v-model="showClose" title="Close cash drawer" @ok="closeShift">
      <p class="small text-muted">
        Expected cash in drawer:
        <strong>{{ formatMoney(currentSession?.expectedCash) }}</strong>
      </p>
      <b-form-group label="Counted cash">
        <b-form-input
          class="form-control"
          v-model="closeForm.countedCash"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-textarea v-model="closeForm.notes" rows="2" />
      </b-form-group>
      <p
        v-if="closeVariance != null"
        class="mb-0"
        :class="varianceClass(closeVariance)"
      >
        Variance: {{ formatMoney(closeVariance) }}
      </p>

      <template #modal-footer="{ ok, cancel }">
        <icon-button
          :func="() => cancel()"
          :icon="''"
          :title="''"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />
        <icon-button
          :func="() => ok()"
          :icon="''"
          :title="''"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Proceed'"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";
import { icon } from "@fortawesome/fontawesome-svg-core";

export default {
  name: "CashDrawer",
  components: {
    IconButton,
  },
  data() {
    return {
      currentSession: null,
      sessions: [],
      loading: false,
      showOpen: false,
      showClose: false,
      openForm: { openingFloat: 0, notes: "" },
      closeForm: { countedCash: 0, notes: "" },
      sessionFields: [
        { key: "openedAt", label: "Opened" },
        { key: "userName", label: "Cashier" },
        { key: "openingFloat", label: "Float" },
        { key: "expectedCash", label: "Expected" },
        { key: "countedCash", label: "Counted" },
        { key: "variance", label: "Variance" },
        { key: "status", label: "Status" },
      ],
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId"]),
    closeVariance() {
      if (!this.currentSession || this.closeForm.countedCash === "")
        return null;
      return (
        Number(this.closeForm.countedCash) -
        Number(this.currentSession?.expectedCash || 0)
      );
    },
  },
  watch: {
    selectedStoreId() {
      this.load();
    },
    showClose(val) {
      if (val && this.currentSession) {
        this.closeForm.countedCash = this.currentSession?.expectedCash;
      }
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDate(iso) {
      if (!iso) return "—";
      return new Date(iso).toLocaleString();
    },
    varianceClass(v) {
      if (v === 0) return "text-success";
      if (v > 0) return "text-primary";
      return "text-danger";
    },
    async load() {
      if (!this.selectedStoreId) return;
      this.loading = true;
      try {
        const params = { storeId: this.selectedStoreId };
        const currentResponse = await ApiService.get(
          "cash-drawer/current",
          params,
        );

        this.currentSession = currentResponse.currentSession;

        const sessionsResponse = await ApiService.get(
          "cash-drawer/sessions",
          params,
        );

        this.sessions = sessionsResponse.sessions;
      } finally {
        this.loading = false;
      }
    },
    async openShift(bvModalEvt) {
      bvModalEvt.preventDefault();
      try {
        await ApiService.post("cash-drawer/open", {
          storeId: this.selectedStoreId,
          openingFloat: Number(this.openForm.openingFloat) || 0,
          notes: this.openForm.notes || null,
        });
        this.showOpen = false;
        this.openForm = { openingFloat: 0, notes: "" };
        await this.load();
        toast.fire({
          title: "Shift opened",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Open failed",
          icon: "danger",
        });
      }
    },
    async closeShift(bvModalEvt) {
      bvModalEvt.preventDefault();
      try {
        await ApiService.post(`cash-drawer/${this.currentSession?.id}/close`, {
          countedCash: Number(this.closeForm.countedCash),
          notes: this.closeForm.notes || null,
        });
        this.showClose = false;
        this.closeForm = { countedCash: 0, notes: "" };
        await this.load();
        toast.fire({
          title: "Shift closed",
          icon: "success",
        });
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Close failed",
          icon: "danger",
        });
      }
    },
  },
};
</script>
